import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import FlagIcon from "@is/components/FlagIcon";
import { CountryCode, LocalStorageKeys } from "@is/constants";
import {
  COUNTRIES_WITH_TRIMMED_PHONE_NUMBER,
  countryPhoneCodes,
} from "@is/constants/countryPhoneCodes";
import { LocalStorageService } from "@is/services";
import { convertAlpha3ToAlpha2 } from "@is/utils";
import { INPUT_NUMBERS } from "@is/utils/regex";

type Props = {
  value?: string;
  onChange?: (name: string) => void;
};

const getPhoneCode = (value: string) => value.split("/")[0];
const getPhoneCountryCode = (value: string) => value.split("/")[1];

function useManagePhoneInput({ value, onChange }: Props) {
  const textInputRef = useRef(null);

  const selectedCountryCode = useMemo(() => {
    if (LocalStorageService.getItem(LocalStorageKeys.PhoneCountryCode)) {
      return LocalStorageService.getItem(LocalStorageKeys.PhoneCountryCode);
    }

    return LocalStorageService.getItem(LocalStorageKeys.CountryCode)
      ? convertAlpha3ToAlpha2(
          LocalStorageService.getItem(LocalStorageKeys.CountryCode)
        )
      : "";
  }, []);

  const countryWithDefaultCountryCode = useMemo(
    () =>
      selectedCountryCode
        ? countryPhoneCodes.find(
            (item) => (item[2] as string).toUpperCase() === selectedCountryCode
          )
        : undefined,
    [selectedCountryCode]
  );

  const [phoneNumber, setPhoneNumber] = useState({
    code: countryWithDefaultCountryCode
      ? `${countryWithDefaultCountryCode[3]}/${countryWithDefaultCountryCode[2]}`
      : "",
    number: value
      ? value.replace(`+${countryWithDefaultCountryCode[3]} `, "")
      : "",
  });

  const phoneCodeOptions = countryPhoneCodes.map((item) => ({
    value: `${item[3]}/${item[2]}`,
    label: ` ${item[0]} +${item[3]}`,
    node: (
      <>
        <FlagIcon countryCode={item[2] as CountryCode} />
      </>
    ),
  }));

  const phoneNumberValue = useMemo(() => {
    const code = getPhoneCode(phoneNumber.code)
      ? `+${getPhoneCode(phoneNumber.code)}`
      : "";
    const value =
      phoneNumber.number === `+${getPhoneCode(phoneNumber.code)} `
        ? " "
        : phoneNumber.number.replace(`+${getPhoneCode(phoneNumber.code)} `, "");

    return value ? `${code} ${value}` : `${code} `;
  }, [phoneNumber]);

  const handleCodeChange = useCallback((values: Array<string>) => {
    const phoneCountryCode = getPhoneCountryCode(values[0]);

    LocalStorageService.setItem(
      LocalStorageKeys.PhoneCountryCode,
      phoneCountryCode.toUpperCase()
    );

    setPhoneNumber({
      code: values[0],
      number: "",
    });

    textInputRef.current.focus();
  }, []);

  const handleNumberChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      let numberWithoutCode = event.target.value.replace(
        `+${getPhoneCode(phoneNumber.code)} `,
        ""
      );

      if (
        COUNTRIES_WITH_TRIMMED_PHONE_NUMBER.includes(
          getPhoneCountryCode(phoneNumber.code).toLowerCase()
        ) &&
        numberWithoutCode?.length &&
        numberWithoutCode[0] === "0"
      ) {
        numberWithoutCode = numberWithoutCode.substring(
          1,
          numberWithoutCode.length
        );
      }

      if (INPUT_NUMBERS.test(numberWithoutCode) || numberWithoutCode === "") {
        setPhoneNumber((prevPhoneNumber) => ({
          ...prevPhoneNumber,
          number:
            event.target.value.indexOf(
              `+${getPhoneCode(phoneNumber.code)} `
            ) !== -1
              ? numberWithoutCode
              : prevPhoneNumber.number,
        }));
      }
    },
    [phoneNumber]
  );

  useEffect(() => {
    const { code, number } = phoneNumber;
    if (code && number && onChange) {
      onChange(`+${getPhoneCode(code)} ${number}`);
    }
  }, [onChange, phoneNumber]);

  useEffect(() => {
    if (!value) {
      setPhoneNumber(() => ({
        code: countryWithDefaultCountryCode
          ? `${countryWithDefaultCountryCode[3]}/${countryWithDefaultCountryCode[2]}`
          : "",
        number: "",
      }));
    }
  }, [countryWithDefaultCountryCode, value]);

  return {
    textInputRef,
    phoneNumber,
    phoneNumberValue,
    phoneCodeOptions,
    handleCodeChange,
    handleNumberChange,
  };
}

export default useManagePhoneInput;
